import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import useSEO from '../../hooks/useSEO';
import './SellerAuth.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const SellerRegister = () => {
  // noindex, matching SellerLogin and every other seller/admin screen. This
  // page carried landing-page metadata from before /sell-taxi existed, and it
  // is now the only page besides the homepage ranking for the brand query
  // "buy sell taxi" — at position 1, on 7 impressions, with zero clicks, while
  // the homepage earns 70.6% on the same term. A 400-word signup form has
  // nothing to offer someone searching the brand, and sell-intent is already
  // served by /sell-taxi (position 4.7, 8.1% CTR), which links here. Keeping it
  // crawlable is deliberate: noindex only works if Google can fetch the page,
  // so the robots.txt Allow for this path must stay.
  useSEO({
    title: 'Create a Seller Account',
    description: 'Create a Buy Sell Taxi seller account to list your taxi or commercial vehicle.',
    url: 'https://www.buyselltaxi.com/seller/register',
    noindex: true,
  });
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', phone: '', email: '', password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      setMessage({ text: 'Passwords do not match', type: 'error' });
      return;
    }
    if (form.password.length < 6) {
      setMessage({ text: 'Password must be at least 6 characters', type: 'error' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      await axios.post(`${API_URL}/seller/register`, {
        name: form.name, phone: form.phone, email: form.email, password: form.password
      });
      setSuccess(true);
    } catch (error) {
      setMessage({ text: error.response?.data?.error || 'Registration failed', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="seller-auth-page">
        <div className="seller-auth-card" data-testid="register-success">
          <div className="seller-auth-logo">🚖</div>
          <h1>Account Created!</h1>
          <p className="success-msg">Please login to continue.</p>
          <Link to="/seller/login" className="btn-primary full-width" data-testid="go-to-login">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="seller-auth-page" data-testid="seller-register-page">
      <div className="seller-auth-card">
        <div className="seller-auth-logo">🚖</div>
        <h1>Buy Sell Taxi</h1>
        <p className="seller-auth-subtitle">Create Seller Account</p>

        {message && <div className={`message message-${message.type}`}>{message.text}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Full Name</label>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" required data-testid="register-name" />
          </div>
          <div className="form-group">
            <label>Phone Number</label>
            <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 XXXXX XXXXX" required data-testid="register-phone" />
          </div>
          <div className="form-group">
            <label>Email Address</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" required data-testid="register-email" />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" required data-testid="register-password" />
          </div>
          <div className="form-group">
            <label>Confirm Password</label>
            <input type="password" value={form.confirmPassword} onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })} placeholder="Re-enter password" required data-testid="register-confirm-password" />
          </div>
          <button type="submit" className="btn-primary full-width" disabled={loading} data-testid="register-submit">
            {loading ? 'Creating Account...' : 'Register'}
          </button>
        </form>

        <p className="auth-link">
          Already have an account? <Link to="/seller/login" data-testid="login-link">Login here</Link>
        </p>
      </div>
    </div>
  );
};

export default SellerRegister;
